import { memo } from 'react'
import { ArrowDown, ArrowUp, ChevronsUp, GripVertical } from 'lucide-react'
import { Badge, DropdownMenu, IconButton, Tooltip } from './common'
import styles from './PostRow.module.css'

// Mirrors the stop() helper from PostOverviewRow.jsx: every interactive
// control nested inside the clickable row must stop the click from bubbling
// to the row's own onClick, or it would also fire onOpen.
function stop(callback) {
  return (event) => {
    event.stopPropagation()
    callback?.()
  }
}

function stopClick(event) {
  event.stopPropagation()
}

function mediaNode(media) {
  if (!media?.url) return <span />
  return media.type === 'video'
    ? <video src={media.url} muted />
    : <img src={media.url} alt="" />
}

function emptyClass(base, isEmpty) {
  return [base, isEmpty ? styles.emptyValue : ''].filter(Boolean).join(' ')
}

const PostRow = memo(function PostRow({
  id,
  rank,
  index,
  total,
  media = null,
  creator,
  title,
  subtitle,
  regulationLabel,
  regulationDetail,
  variantCount,
  selectionCount,
  createdLabel,
  chips = [],
  // status is one badge on its own line under the post copy (e.g. why a
  // post is not live), so it never competes with the creator and text.
  status = null,
  selected,
  onSelectedChange,
  active = false,
  draggable = false,
  isDragged = false,
  isOver = false,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onMoveToTop,
  onMoveUp,
  onMoveDown,
  onOpen,
  onPreviewEnter,
  onPreviewMove,
  onPreviewLeave,
  menuItems = [],
  orderActionsDisabled = false,
  orderSlots = 3,
  columns = {},
}) {
  const showSelection = selected !== undefined
  const showVariants = columns.variants !== false
  const hasOrderActions = Boolean(onMoveToTop || onMoveUp || onMoveDown)
  const hasMenu = menuItems.length > 0

  return (
    <article
      className={[
        styles.row,
        showSelection ? styles.withSelection : '',
        showVariants ? '' : styles.noVariants,
        active ? styles.active : '',
        isDragged ? styles.dragging : '',
        isOver ? styles.over : '',
      ].filter(Boolean).join(' ')}
      style={{ '--wf-post-row-order-slots': orderSlots }}
      data-testid={`post-row-${id}`}
      role="listitem"
      aria-label={title}
      aria-current={active ? 'true' : undefined}
      tabIndex={0}
      draggable={draggable}
      onClick={() => onOpen?.()}
      onKeyDown={(event) => {
        // Ignore keydowns that bubbled up from a nested control (checkbox,
        // reorder button, menu trigger) — those already handle their own
        // Enter/Space and must not also open the row.
        if (event.target !== event.currentTarget) return
        if (event.key === 'Enter') {
          onOpen?.()
        } else if (event.key === ' ') {
          event.preventDefault()
          onOpen?.()
        }
      }}
      onDragStart={() => onDragStart?.(id)}
      onDragOver={(event) => {
        if (!draggable) return
        event.preventDefault()
        onDragOver?.(id)
      }}
      onDrop={() => onDrop?.(id)}
      onDragEnd={onDragEnd}
    >
      {/* The whole cell is the label, so the activation area — not just the
          1rem glyph — is what a finger has to hit; the 48rem tier grows it to
          the 44px touch-target minimum while the glyph stays native size. */}
      {showSelection ? (
        <label className={styles.checkboxCell} onClick={stopClick}>
          <input
            type="checkbox"
            aria-label={`Select ${title}`}
            checked={Boolean(selected)}
            onChange={(event) => onSelectedChange?.(event.target.checked)}
          />
        </label>
      ) : null}

      <div className={styles.rankBlock} aria-label={`Position ${rank} of ${total}`}>
        {draggable ? <GripVertical size={16} aria-hidden="true" /> : null}
        <strong className="wf-tabular-nums">{rank}</strong>
      </div>

      {/* display:contents keeps thumbnail+post as direct grid children (so the
          fixed column template still applies to each) while letting a single
          wrapper own the hover-preview mouse handlers for both cells. */}
      <div
        className={styles.previewZone}
        onMouseEnter={onPreviewEnter}
        onMouseMove={onPreviewMove}
        onMouseLeave={onPreviewLeave}
      >
        <div className={styles.thumbnail} aria-hidden="true">
          {mediaNode(media)}
        </div>

        <div className={styles.post}>
          <div className={styles.postPrimary}>
            <span className={styles.creator} title={creator || undefined}>{creator || 'Unknown creator'}</span>
            <span className={styles.title} title={title || undefined}>{title}</span>
            {chips.length ? (
              <span className={styles.postChips}>
                {chips.map((chip) => (
                  <Badge key={chip.key} compact tone={chip.tone} title={chip.label}>{chip.label}</Badge>
                ))}
              </span>
            ) : null}
          </div>
          {/* An empty subtitle line only holds the row's height; a status
              line takes its place instead of adding a third line. */}
          {subtitle || !status
            ? <span className={styles.subtitle} title={subtitle || undefined}>{subtitle}</span>
            : null}
          {status ? (
            <span className={styles.postStatus}>
              <Badge compact tone={status.tone} title={status.label}>{status.label}</Badge>
            </span>
          ) : null}
        </div>
      </div>

      <div className={styles.regulations}>
        <span className={emptyClass(styles.regulationLabel, !regulationLabel)}>{regulationLabel || '—'}</span>
        <span className={styles.regulationDetail}>{regulationDetail}</span>
      </div>

      {showVariants ? (
        <div className={styles.variants}>
          {variantCount > 0
            ? <Badge compact tone="info">{variantCount} custom</Badge>
            : <span className={styles.emptyValue}>—</span>}
        </div>
      ) : null}

      <div className={styles.selections}>
        {selectionCount > 0 ? <span className="wf-tabular-nums">{selectionCount}</span> : null}
      </div>

      <div className={styles.created}>
        <span className={emptyClass('wf-tabular-nums', !createdLabel)}>{createdLabel || '—'}</span>
      </div>

      {/* Same display:contents trick: reorder buttons + overflow menu stay as
          two independent trailing columns until the 48rem tier collapses
          this wrapper into one flex-wrapped "actions" area. */}
      <div className={styles.actionsGroup}>
        <div
          className={styles.orderActions}
          role={hasOrderActions ? 'group' : undefined}
          aria-label={hasOrderActions ? `Reorder ${title}` : undefined}
        >
          {onMoveToTop ? (
            <Tooltip content="Move to top">
              <IconButton label={`Move ${title} to top`} variant="ghost" onClick={stop(onMoveToTop)} disabled={orderActionsDisabled || rank === 1}>
                <ChevronsUp size={16} aria-hidden="true" />
              </IconButton>
            </Tooltip>
          ) : null}
          {onMoveUp ? (
            <Tooltip content="Move up">
              <IconButton label={`Move ${title} up`} variant="ghost" onClick={stop(onMoveUp)} disabled={orderActionsDisabled || index === 0}>
                <ArrowUp size={16} aria-hidden="true" />
              </IconButton>
            </Tooltip>
          ) : null}
          {onMoveDown ? (
            <Tooltip content="Move down">
              <IconButton label={`Move ${title} down`} variant="ghost" onClick={stop(onMoveDown)} disabled={orderActionsDisabled || index >= total - 1}>
                <ArrowDown size={16} aria-hidden="true" />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>

        <div className={styles.menuCell} onClick={stopClick}>
          {hasMenu ? (
            <DropdownMenu triggerLabel={`More actions for ${title}`} items={menuItems} />
          ) : null}
        </div>
      </div>
    </article>
  )
})

// Sticky column-label row. Renders the identical grid (via the shared .grid
// class) so its cells line up with every PostRow beneath it. Columns that
// carry no visible label (rank, thumbnail, reorder, menu) still get an empty
// cell so the template never shifts.
//
// The row is aria-hidden: it is a purely visual alignment aid. Nothing in it
// is interactive, and because the queue is a list rather than a table these
// labels have no programmatic association with the cells below them — to a
// screen reader they would only read as loose words wedged between the list
// and its items. Keeping the row out of the accessibility tree is also what
// leaves every child the queue's role="list" owns a real listitem. Selecting
// every loaded row lives in the queue summary's "Select page (N)" control,
// which renders under exactly the same condition this header does, so there
// is no select-all checkbox here to duplicate it.
export function PostRowHeader({
  showSelection = false,
  columns = {},
  orderSlots = 3,
}) {
  const showRegulations = columns.regulations !== false
  const showVariants = columns.variants !== false

  return (
    <div
      className={[
        styles.headerRow,
        showSelection ? styles.withSelection : '',
        showVariants ? '' : styles.noVariants,
      ].filter(Boolean).join(' ')}
      style={{ '--wf-post-row-order-slots': orderSlots }}
      aria-hidden="true"
    >
      {showSelection ? <div className={styles.checkboxCell} /> : null}

      <div className={styles.rankBlock} />

      <div className={`${styles.thumbnail} ${styles.headerThumb}`} />

      <div className={styles.post}>
        <span className={styles.headerLabel}>Post</span>
      </div>

      <div className={styles.regulations}>
        <span className={showRegulations ? styles.headerLabel : 'wf-visually-hidden'}>Regulations</span>
      </div>

      {showVariants ? (
        <div className={styles.variants}>
          <span className={styles.headerLabel}>Variants</span>
        </div>
      ) : null}

      <div className={styles.selections}>
        <span className={styles.headerLabel}>Selections</span>
      </div>

      <div className={styles.created}>
        <span className={styles.headerLabel}>Created</span>
      </div>

      <div className={styles.actionsGroup}>
        <div className={styles.orderActions} />
        <div className={styles.menuCell} />
      </div>
    </div>
  )
}

export default PostRow
